import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Tracks OTT-specific performance metrics via Sentry:
 * - Video playback startup time
 * - Buffering/stall events
 * - Image & thumbnail load times
 * - Page/route performance (handled automatically by Sentry's browserTracingIntegration)
 *
 * Usage:
 *   this.sentryPerf.startVideoLoad(videoId);        // when player starts loading
 *   this.sentryPerf.endVideoLoad(videoId);           // when first frame plays
 *   this.sentryPerf.trackBuffering(videoId);         // when buffering starts
 *   this.sentryPerf.trackBufferingEnd(videoId);      // when buffering ends
 *   this.sentryPerf.trackImageLoad(imageUrl, durationMs);
 */
@Injectable({ providedIn: 'root' })
export class SentryPerformanceService {

  private activeSpans = new Map<string, ReturnType<typeof Sentry.startInactiveSpan>>();
  private bufferStartTimes = new Map<string, number>();

  constructor() {
    this.observeSlowResources();
  }

  // ── Auto-track slow image/resource loads via PerformanceObserver ──

  private observeSlowResources() {
    if (typeof PerformanceObserver === 'undefined') return;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const resource = entry as PerformanceResourceTiming;
          // Only track images that took > 1 second (slow for user experience)
          if (resource.initiatorType === 'img' && resource.duration > 1000) {
            this.trackImageLoad(resource.name, resource.duration);
          }
        }
      });
      observer.observe({ type: 'resource', buffered: false });
    } catch (e) {
      // PerformanceObserver not supported — skip silently
    }
  }

  // ── Video Playback Startup ──────────────────────────────────

  startVideoLoad(videoId: string, metadata?: { title?: string; channelId?: string; quality?: string }) {
    const span = Sentry.startInactiveSpan({
      name: 'video.startup',
      op: 'media.load',
      attributes: {
        'video.id': videoId,
        'video.title': metadata?.title || '',
        'video.channel_id': metadata?.channelId || '',
        'video.quality': metadata?.quality || 'auto',
      },
    });
    if (span) {
      this.activeSpans.set(`video-load-${videoId}`, span);
    }
  }

  endVideoLoad(videoId: string) {
    const key = `video-load-${videoId}`;
    const span = this.activeSpans.get(key);
    if (span) {
      span.end();
      this.activeSpans.delete(key);
    }
  }

  // ── Buffering / Stall Tracking ──────────────────────────────

  trackBuffering(videoId: string) {
    this.bufferStartTimes.set(videoId, performance.now());

    const span = Sentry.startInactiveSpan({
      name: 'video.buffering',
      op: 'media.buffer',
      attributes: { 'video.id': videoId },
    });
    if (span) {
      this.activeSpans.set(`buffer-${videoId}`, span);
    }
  }

  trackBufferingEnd(videoId: string) {
    const bufferKey = `buffer-${videoId}`;
    const span = this.activeSpans.get(bufferKey);
    const startTime = this.bufferStartTimes.get(videoId);

    if (span) {
      const durationMs = startTime ? performance.now() - startTime : 0;
      span.setAttribute('buffer.duration_ms', Math.round(durationMs));
      span.end();
      this.activeSpans.delete(bufferKey);
    }
    this.bufferStartTimes.delete(videoId);
  }

  // ── Image / Thumbnail Loading ───────────────────────────────

  trackImageLoad(imageUrl: string, durationMs: number) {
    const span = Sentry.startInactiveSpan({
      name: 'image.load',
      op: 'resource.img',
      attributes: {
        'image.url': imageUrl,
        'image.duration_ms': Math.round(durationMs),
      },
    });
    if (span) {
      span.end();
    }
  }

  // ── DRM License Acquisition ─────────────────────────────────

  startDrmLicense(videoId: string) {
    const span = Sentry.startInactiveSpan({
      name: 'drm.license',
      op: 'media.drm',
      attributes: { 'video.id': videoId },
    });
    if (span) {
      this.activeSpans.set(`drm-${videoId}`, span);
    }
  }

  endDrmLicense(videoId: string) {
    const key = `drm-${videoId}`;
    const span = this.activeSpans.get(key);
    if (span) {
      span.end();
      this.activeSpans.delete(key);
    }
  }

  // ── ABR Quality Switches ─────────────────────────────────────

  trackQualityChange(videoId: string, fromHeight: number, toHeight: number, bandwidth: number) {
    Sentry.addBreadcrumb({
      category: 'video.quality',
      message: `Quality changed: ${fromHeight}p → ${toHeight}p`,
      level: 'info',
      data: {
        video_id: videoId,
        from_quality: fromHeight,
        to_quality: toHeight,
        bandwidth_bps: bandwidth,
      },
    });
  }

  // ── API Response Timing ─────────────────────────────────────

  trackApiCall(method: string, url: string, durationMs: number, status: number) {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${method} ${url}`,
      level: status >= 400 ? 'error' : 'info',
      data: {
        method,
        url,
        status_code: status,
        duration_ms: Math.round(durationMs),
      },
    });
  }

  // ── Network Quality ─────────────────────────────────────────

  trackNetworkChange(status: string, downlink?: number, effectiveType?: string) {
    Sentry.addBreadcrumb({
      category: 'network',
      message: `Network: ${status}`,
      level: status === 'offline' ? 'error' : status === 'poor' ? 'warning' : 'info',
      data: {
        status,
        downlink_mbps: downlink,
        effective_type: effectiveType,
      },
    });
    Sentry.setTag('network.status', status);
    if (effectiveType) {
      Sentry.setTag('network.effective_type', effectiveType);
    }
  }

  // ── Set User/Region Context ─────────────────────────────────

  setUserContext(userId: string, region?: string, channelId?: string) {
    Sentry.setUser({ id: userId });
    if (region) {
      Sentry.setTag('user.region', region);
    }
    if (channelId) {
      Sentry.setTag('channel.id', channelId);
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────

  cleanup() {
    this.activeSpans.forEach(span => span?.end());
    this.activeSpans.clear();
    this.bufferStartTimes.clear();
  }
}
